import type { TimeseriesDayPoint } from "@/api/campaign";

/**
 * Drop the leading days with no movement — the first observation of every video
 * is zero growth, so the line would otherwise always open flat.
 */
export function dropLeadingZeroDays(series: TimeseriesDayPoint[]): TimeseriesDayPoint[] {
	const firstValue = series.findIndex((point) => point.views > 0 || point.engagements > 0);
	if (firstValue <= 0) return series;
	return series.slice(firstValue);
}

export function isSparseObservationSeries(days: string[]): boolean {
	return days.some((day, index) => {
		if (index === 0) return false;
		const current = Date.parse(`${day}T00:00:00Z`);
		const previous = Date.parse(`${days[index - 1]}T00:00:00Z`);
		return Number.isFinite(current) && Number.isFinite(previous) && current - previous > 36 * 60 * 60 * 1000;
	});
}

/**
 * Leave a small amount of room below the lowest value without forcing the
 * scale to zero or allowing Chart.js grace to create negative ticks.
 */
export function metricScaleMinimum(values: number[]): number {
	const minimum = Math.min(...values.filter((value) => Number.isFinite(value)));
	if (!Number.isFinite(minimum) || minimum <= 0) return 0;
	return minimum * 0.9;
}

export function formatMetricTick(value: number, ticks: number[] = []): string {
	const n = Number(value);
	if (!Number.isFinite(n)) return "";
	if (n === 0) return "0";

	const tickNums = ticks.map(Number).filter((tick) => Number.isFinite(tick));
	const span = tickNums.length >= 2 ? Math.max(...tickNums) - Math.min(...tickNums) : 0;

	if (n >= 1_000_000) {
		const decimals = span > 0 && span < 100_000 ? 3 : span > 0 && span < 1_000_000 ? 2 : 1;
		return `${(n / 1_000_000).toFixed(decimals)}m`;
	}
	if (n >= 1000) {
		const decimals = span > 0 && span < 1000 ? 1 : 0;
		return `${(n / 1000).toFixed(decimals)}k`;
	}
	return String(Math.round(n));
}

const DAY_MS = 24 * 60 * 60 * 1000;
const METRIC_KEYS = ["views", "engagements", "likes", "comments", "shares", "saves"] as const;

/** The Monday (UTC) of the week a `YYYY-MM-DD` day falls in, as `YYYY-MM-DD`. */
export function weekStart(day: string): string {
	const time = Date.parse(`${day}T00:00:00Z`);
	const daysSinceMonday = (new Date(time).getUTCDay() + 6) % 7;
	return new Date(time - daysSinceMonday * DAY_MS).toISOString().slice(0, 10);
}

/** Sums daily points into Monday-start weeks; each week's `date` is its Monday. */
export function groupByWeek(series: TimeseriesDayPoint[]): TimeseriesDayPoint[] {
	const weeks = new Map<string, TimeseriesDayPoint>();
	[...series]
		.sort((left, right) => left.date.localeCompare(right.date))
		.forEach((point) => {
			const date = weekStart(point.date);
			const week = weeks.get(date) ?? {
				date,
				views: 0,
				engagements: 0,
				likes: 0,
				comments: 0,
				shares: 0,
				saves: 0,
			};
			METRIC_KEYS.forEach((key) => {
				week[key] += point[key] || 0;
			});
			weeks.set(date, week);
		});
	return Array.from(weeks.values());
}

export interface SeriesWeek {
	/** Monday, `YYYY-MM-DD`. */
	start: string;
	/** Monday to Sunday; `null` where the series has no point for that day. */
	days: Array<{ date: string; point: TimeseriesDayPoint | null }>;
}

/** The weeks (Monday to Sunday, oldest first) that contain at least one point of the series. */
export function seriesWeeks(series: TimeseriesDayPoint[]): SeriesWeek[] {
	const byDay = new Map(series.map((point) => [point.date, point]));
	const starts = Array.from(new Set(series.map((point) => weekStart(point.date)))).sort();
	return starts.map((start) => {
		const monday = Date.parse(`${start}T00:00:00Z`);
		return {
			start,
			days: Array.from({ length: 7 }, (_, index) => {
				const date = new Date(monday + index * DAY_MS).toISOString().slice(0, 10);
				return { date, point: byDay.get(date) ?? null };
			}),
		};
	});
}

/**
 * A typical day's value for a metric: the median of its non-zero days, so one viral spike or a run
 * of empty days doesn't skew it. 0 when every day is zero.
 */
export function typicalDailyValue(values: number[]): number {
	const positive = values.filter((value) => Number.isFinite(value) && value > 0).sort((a, b) => a - b);
	if (positive.length === 0) return 0;
	const middle = Math.floor(positive.length / 2);
	return positive.length % 2 ? positive[middle] : (positive[middle - 1] + positive[middle]) / 2;
}

/** Step sizes that read as even numbers on an axis (0 / 60K / 120K, 0 / 2.5M / 5M), per power of ten. */
const EVEN_STEPS = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];

/**
 * A zero-based axis with `intervals` equal, even steps (see EVEN_STEPS) and the smallest top that
 * still clears the largest value by 5%, so the peak sits high on the chart without odd labels like
 * 53K. Two axes built with the same `intervals` share their gridlines.
 */
export function tightAxis(values: number[], intervals = 4): { max: number; stepSize: number } {
	const largest = Math.max(0, ...values.filter((value) => Number.isFinite(value)));
	if (largest === 0) return { max: intervals, stepSize: 1 };
	const rough = (largest * 1.05) / intervals;
	const magnitude = 10 ** Math.floor(Math.log10(rough));
	// The epsilon keeps float noise (420000.0000001) from skipping an exact fit.
	const stepSize = EVEN_STEPS.map((factor) => factor * magnitude).find((step) => step >= rough - 1e-9 * magnitude)!;
	return { max: stepSize * intervals, stepSize };
}
